import { useState, useEffect } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { UserCircle, X, Plus, Star } from "@phosphor-icons/react";
import { useRamsGuard } from "./hs/RamsGuard";

/**
 * Admin widget to manage the in-house crew on a job or emergency_job (one or many operatives).
 * The first operative is the lead. Every change posts the full list to /{job|emergency_job}/{id}/assign-operative.
 * - entityType: "job" | "emergency_job"
 * - currentOperativeIds / currentOperativeNames: arrays from the record (legacy single fields accepted via fallback)
 * - hasRams: false → adding an operative first asks "Continue without RAMS?" (once per job per session); omit to skip the check
 * - onChange: callback invoked after a successful change (caller refetches)
 */
export default function InHouseOperativeAssigner({
  entityType, entityId, currentOperativeIds, currentOperativeNames, currentOperativeId, currentOperativeName, onChange, hasRams,
}) {
  const ids = (currentOperativeIds && currentOperativeIds.length) ? currentOperativeIds : (currentOperativeId ? [currentOperativeId] : []);
  const names = (currentOperativeNames && currentOperativeNames.length) ? currentOperativeNames : (currentOperativeName ? [currentOperativeName] : []);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const { guard, dialog: ramsDialog } = useRamsGuard({ entityType, entityId, hasRams });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    API.get("/in-house/team")
      .then(({ data }) => { if (!cancelled) setTeam(data.team || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async (nextIds, msg) => {
    if (nextIds.length > ids.length) return guard(() => doSave(nextIds, msg));
    return doSave(nextIds, msg);
  };
  const doSave = async (nextIds, msg) => {
    setSaving(true);
    try {
      await API.post(`/${entityType === "emergency_job" ? "emergency_job" : "job"}/${entityId}/assign-operative`, { operative_ids: nextIds });
      toast.success(msg);
      setAdding(false);
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update assignment");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-xs text-muted-foreground italic">Loading team…</p>;
  if (!team.length && !ids.length) {
    return <p className="text-xs text-muted-foreground italic">No in-house team yet. Add operatives under Settings → In-House Team.</p>;
  }

  const available = team.filter(u => !ids.includes(u._id));
  const nameFor = (id, i) => names[i] || team.find(u => u._id === id)?.name || "Operative";

  return (
    <div className="space-y-2" data-testid="in-house-crew">
      {ramsDialog}
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="in-house-operative-current">
          {ids.map((id, i) => (
            <span key={id} className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 pl-2 pr-1 py-0.5 text-xs" data-testid={`in-house-crew-${id}`}>
              {i === 0 ? <Star size={11} weight="fill" className="text-amber-600" title="Lead" /> : <UserCircle size={12} className="text-amber-600" />}
              <span className="font-medium">{nameFor(id, i)}</span>
              <button onClick={() => save(ids.filter(x => x !== id), "Operative removed")} disabled={saving}
                className="rounded-full p-0.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-50"
                title="Remove from job" data-testid={`in-house-crew-remove-${id}`}>
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
      {ids.length > 1 && <p className="text-[10px] text-muted-foreground">★ lead · {ids.length} operatives — each checks in on site separately.</p>}
      {!adding ? (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAdding(true)} disabled={saving || available.length === 0} data-testid="in-house-crew-add-btn">
            <Plus size={12} className="mr-1" /> {ids.length ? "Add operative" : "Assign operative"}
          </Button>
          {ids.length > 1 && (
            <button onClick={() => save([], "Crew unassigned")} disabled={saving} className="text-xs text-red-600 hover:text-red-700" data-testid="in-house-operative-unassign">Unassign all</button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Select onValueChange={(v) => save([...ids, v], ids.length ? "Operative added to crew" : "Operative assigned")} disabled={saving}>
            <SelectTrigger className="text-xs h-8 flex-1" data-testid="in-house-operative-select">
              <SelectValue placeholder={saving ? "Saving…" : "Select operative…"} />
            </SelectTrigger>
            <SelectContent>
              {available.map((u) => <SelectItem key={u._id} value={u._id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}
    </div>
  );
}
